/**
 * CashbackBR — Paranoid Session Management (Auto-Lock)
 *
 * Monitors user activity. After 180s of inactivity, destroys
 * the auth session and renders a lock screen requiring re-auth.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface IdleTimeoutOptions {
  /** Idle threshold in seconds. Default: 180 (3 minutes). */
  timeoutSeconds?: number;
  /** Whether the hook is active. Default: true. */
  enabled?: boolean;
}

export function useIdleTimeout(options: IdleTimeoutOptions = {}) {
  const { timeoutSeconds = 180, enabled = true } = options;
  const [isLocked, setIsLocked] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockedRef = useRef(false);

  const lock = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setIsLocked(true);
    // Don't sign out — just lock the screen. User re-auths to unlock.
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled || lockedRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(lock, timeoutSeconds * 1000);
  }, [enabled, timeoutSeconds, lock]);

  const unlock = useCallback(async () => {
    // Verify current session is still valid before unlocking
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      // Session expired — force full re-auth
      await supabase.auth.signOut();
      window.location.href = "/auth";
      return false;
    }
    lockedRef.current = false;
    setIsLocked(false);
    resetTimer();
    return true;
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) return;

    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "touchstart",
      "scroll",
      "click",
    ];

    const handleActivity = () => resetTimer();

    events.forEach((e) =>
      window.addEventListener(e, handleActivity, { passive: true }),
    );
    resetTimer(); // Start the timer

    // Also lock on visibility change (tab hidden / screen off)
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        // Start a shorter timer when app goes to background
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(
          lock,
          Math.min(timeoutSeconds, 60) * 1000,
        );
      } else if (!lockedRef.current) {
        resetTimer();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, resetTimer, lock, timeoutSeconds]);

  return { isLocked, unlock, lock, resetTimer };
}
